import tw, { css } from "twin.macro";
import React from "react";
import { Icon, type IconComponent } from "~/features/ui/icon";
import { hasItems } from "~/features/app/utils";
import { omitGraphQLMetaProps } from "~/features/ui/dom-props";
import { hover } from "~/features/ui/utils";

export type ButtonVariant = keyof ReturnType<typeof getVariants>;

export type Props = {
  text?: React.ReactNode;
  variant?: ButtonVariant;
  size?: keyof typeof sizes;
  icon?: IconComponent;
  fullHeight?: boolean;
  fullWidth?: boolean;
  rounded?: boolean;
  justifyContent?: keyof typeof justifyContentStyles;
  children?: React.ReactNode;
  hoverId?: string;
  [key: string]: unknown;
};

const buttonStyles = tw`
  relative inline-flex shrink-0 items-center justify-between overflow-hidden text-mobile-cta-20-normal whitespace-nowrap
  lg:text-desktop-cta-20-normal
  transition-colors duration-250 ease-in-out
  disabled:(pointer-events-none text-black-transparent-10)
`;

const iconStyles = tw`
  h-full flex items-center justify-center
  transition-colors duration-250 ease-in-out
`;

const sizes = {
  default: tw`h-48 lg:h-56`,
  consistent: tw`h-56`,
  small: tw`h-36 lg:h-40`,
};

const justifyContentStyles = {
  between: tw`justify-between`,
  center: tw`justify-center`,
};

function getVariants(props: { hoverId?: string }) {
  const { hoverId } = props;

  return {
    primary: css`
      ${tw`
        bg-green-80 text-white-100-light
        [> [data-text]]:(px-16 lg:px-40)
        [> [data-icon]]:(w-40 border-l bg-green-100 border-green-80 transition-colors duration-250 ease-in-out)
        can-hover:hover:bg-green-100
        active:(bg-green-80! [> [data-icon]]:bg-green-80)
        disabled:(bg-grey-50-light [> [data-icon]]:(bg-grey-50-light text-black-80-light border-black-transparent-10))
      `}
      ${hover(hoverId, tw`bg-green-100`)}
    `,
    transparent: css`
      ${tw`
        bg-black-transparent-40 text-white-100-light gap-x-8 px-16 lg:px-24
        active:bg-black-transparent-40!
        disabled:bg-black-transparent-20-light
      `}
      ${hover(hoverId, tw`bg-black-transparent-60`)}
    `,
    secondary: css`
      ${tw`
        bg-green-80 text-white-100-light gap-x-8 px-16 lg:px-24
        active:bg-green-80!
        disabled:bg-grey-50-light
      `}
      ${hover(hoverId, tw`bg-green-100`)}
    `,
    secondaryBlue: css`
      ${tw`
        bg-blue-100 text-white-100-light gap-x-8 px-16 lg:px-24
        active:bg-blue-100!
        disabled:bg-grey-50-light
      `}
      ${hover(hoverId, tw`bg-blue-80`)}
    `,
    underline: css`
      ${tw`
        gap-x-6 transition-opacity duration-250 ease-in-out pb-5 h-auto overflow-visible
        lg:(h-auto gap-x-8)
        before:(content-[""] absolute bottom-0 left-0 w-full h-1 bg-blue-100 origin-bottom transition-transform duration-250 ease-in-out)
        disabled:before:bg-grey-50-light
        active:text-black-transparent-60
      `}
      ${hover(hoverId, tw`before:[transform: scaleY(2)]`)}
    `,
    black: css`
      ${tw`
        bg-black-100-light text-white-100-light gap-x-8 px-16 lg:px-24
      `}
      ${hover(hoverId, tw`bg-blue-100`)}
    `,
    // --- UI 3.0 CTA design system (solid / outline / ghost) --------------------
    // Solid indigo primary. Inline arrow, 2px radius. Static blue reads on both
    // light and dark section backgrounds, so no theme flip is needed here.
    solid: css`
      ${tw`
        bg-blue-100 text-white-100-light gap-x-8 px-16 lg:px-24
        can-hover:hover:bg-blue-80
        active:bg-blue-100!
        disabled:(bg-grey-50-light text-black-transparent-10)
      `}
      ${hover(hoverId, tw`bg-blue-80`)}
    `,
    // Outline secondary. Border + label use the semantic stroke/text tokens,
    // which flip automatically with the section's `data-theme`, so the same
    // variant works in light and dark. Hover/active pick up the indigo accent.
    outline: css`
      ${tw`
        bg-transparent border border-stroke-primary text-primary gap-x-8 px-16 lg:px-24
        can-hover:hover:(border-blue-100 text-blue-100)
        active:(border-blue-100! text-blue-100)
        disabled:(border-black-transparent-10 text-black-transparent-10)
      `}
      ${hover(hoverId, tw`border-blue-100 text-blue-100`)}
    `,
    // Text-only tertiary ("text button"). No fill/border; theme-aware label with
    // an indigo hover accent.
    ghost: css`
      ${tw`
        gap-x-6 px-0 text-primary
        lg:gap-x-8
        can-hover:hover:text-blue-100
        active:text-blue-100
        disabled:text-black-transparent-10
      `}
      ${hover(hoverId, tw`text-blue-100`)}
    `,
  };
}

function normalizeProps(props: Props) {
  const {
    text,
    children,
    variant = "primary",
    size = "default",
    fullHeight = false,
    fullWidth = false,
    rounded = true,
    justifyContent = "between",
    hoverId,
    icon,
    ...rest
  } = props;
  const domProps = omitGraphQLMetaProps(rest);

  return {
    styles: [
      buttonStyles,
      sizes[size],
      getVariants({ hoverId })[variant],
      fullHeight && tw`h-full!`,
      fullWidth && tw`w-full!`,
      rounded && variant !== "underline" && tw`rounded-2`,
      justifyContentStyles[justifyContent],
    ],
    children: hasItems(React.Children.toArray(children)) ? children : text,
    icon,
    ...domProps,
  };
}

export function Button(props: Props & React.ComponentPropsWithRef<"button">) {
  const { styles, children, icon, ...rest } = normalizeProps(props);

  return (
    <button {...rest} css={styles}>
      <span tw="inline-flex items-center" data-text>
        {children}
      </span>
      {icon && (
        <span data-icon css={iconStyles}>
          <Icon icon={icon} />
        </span>
      )}
    </button>
  );
}
